import React from 'react';
import { useLazyRef } from '@shopify/react-hooks';
import { useSerialized, useHtmlAttributes } from '@shopify/react-html';
import { I18nManager, I18nContext } from '@shopify/react-i18n';
import { combinedI18nDetails } from './utilities.mjs';

/**
 * A self-serializing provider for `@shopify/i18n`'s I18nManager.
 * On the server it serializes its props to the DOM; on the client it retrieves the serialized props and rehydrates them.
 *
 * @param props an object containing the React children to render and any options to use when configuring the tree's `I18nManager`
 * @returns JSX wrapping the given children in an `<I18nContext.Provider>` and a `<Serialize />` component.
 */
function I18nUniversalProvider({
  children,
  ...explicitI18nDetails
}) {
  const [serialized, Serialize] = useSerialized('i18n');
  const i18nDetails = combinedI18nDetails(serialized, explicitI18nDetails);
  const manager = useLazyRef(() => new I18nManager(i18nDetails, serialized === null || serialized === void 0 ? void 0 : serialized.translations)).current;
  useHtmlAttributes({
    lang: i18nDetails.locale
  });
  const {
    onError,
    ...primitiveI18nDetails
  } = i18nDetails;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(I18nContext.Provider, {
    value: manager
  }, children), /*#__PURE__*/React.createElement(Serialize, {
    data: () => {
      const getData = () => ({
        translations: manager.extract(),
        ...primitiveI18nDetails
      });
      if (manager.loading) {
        return manager.resolve().then(getData);
      }
      return getData();
    }
  }));
}

export { I18nUniversalProvider };
